/**
 * DataTab — Data stage with in-tab vertical sub-rail (Phase 4.3 / Phase 5.3)
 *
 * Consolidates data-related tools into a single stage:
 * - Reconciliation Workbench
 * - Anomaly Review
 * - Data Quality Report
 * - Cleanup Validation
 * - Batch OCR
 * - Document Intelligence
 *
 * Note: Transaction Review and Issue Tracker moved to Unified Work Queue (/queue)
 * in Phase 5.3 as part of the UNIFIED_WORK_QUEUE consolidation.
 *
 * Design spec compliance:
 *   - CSS variable tokens ONLY (no literal hex values)
 *   - Near-black surfaces, orange brand accent, green for done
 *   - DM Serif Display / Outfit / DM Mono type stack
 *   - Vertical sub-rail ~200px with near-black background
 */

import React, { useState, useMemo, Suspense } from 'react';
import { Database, AlertTriangle, FileCheck, Trash2, Upload, FileText } from 'lucide-react';

// ============================================================================
// LAZY IMPORTS — Heavy components loaded on demand
// ============================================================================

// @ts-ignore — Component may not exist yet; guarded by try/catch pattern in render
const AccountantReconciliation = React.lazy(() => import('@/pages/AccountantReconciliation'));
// @ts-ignore
const AccountantAnomalyReview = React.lazy(() => import('@/pages/AccountantAnomalyReview'));
const DataQualityReportPanel = React.lazy(() => import('@/components/data-quality/DataQualityReportPanel'));
// @ts-ignore
const CleanupValidation = React.lazy(() => import('@/pages/CleanupValidation'));
// @ts-ignore
const BatchProcessingPage = React.lazy(() => import('@/pages/BatchProcessingPage'));
// @ts-ignore
const DataReviewPage = React.lazy(() => import('@/pages/DataReviewPage'));

// ============================================================================
// TYPES
// ============================================================================

export type DataSubTab =
  | 'reconciliation'
  | 'anomalies'
  | 'data-quality'
  | 'cleanup'
  | 'batch-ocr'
  | 'doc-intelligence';

interface SubRailItem {
  id: DataSubTab;
  label: string;
  icon: React.ElementType;
  component: React.LazyExoticComponent<React.ComponentType<any>>;
  requiresCaseId?: boolean;
}

// ============================================================================
// SUB-RAIL CONFIGURATION
// ============================================================================

const SUB_RAIL_ITEMS: SubRailItem[] = [
  {
    id: 'reconciliation',
    label: 'Reconciliation',
    icon: Database,
    component: AccountantReconciliation,
    requiresCaseId: true,
  },
  {
    id: 'anomalies',
    label: 'Anomalies',
    icon: AlertTriangle,
    component: AccountantAnomalyReview,
    requiresCaseId: true,
  },
  {
    id: 'data-quality',
    label: 'Data Quality',
    icon: FileCheck,
    component: DataQualityReportPanel,
    requiresCaseId: true,
  },
  // Phase 5.3: Transaction review moved to /queue (unified work queue)
  {
    id: 'cleanup',
    label: 'Cleanup Validation',
    icon: Trash2,
    component: CleanupValidation,
    requiresCaseId: true,
  },
  {
    id: 'batch-ocr',
    label: 'Batch OCR',
    icon: Upload,
    component: BatchProcessingPage,
    requiresCaseId: false,
  },
  // Phase 5.3: Issue tracker moved to /queue (unified work queue)
  {
    id: 'doc-intelligence',
    label: 'Doc Intelligence',
    icon: FileText,
    component: DataReviewPage,
    requiresCaseId: false,
  },
];

// ============================================================================
// PROPS
// ============================================================================

export interface DataTabProps {
  /** Case/report ID for case-scoped tools */
  caseId?: string;
  /** Optional initial selected sub-tab */
  defaultSubTab?: DataSubTab;
  /** Optional CSS class */
  className?: string;
}

// ============================================================================
// SUB-COMPONENTS
// ============================================================================

interface SubRailProps {
  items: SubRailItem[];
  activeId: DataSubTab;
  onSelect: (id: DataSubTab) => void;
}

function SubRail({ items, activeId, onSelect }: SubRailProps): React.JSX.Element {
  return (
    <aside
      className="w-[200px] flex-shrink-0 flex flex-col border-r border-[var(--color-border)] bg-[var(--color-bg-surface)]"
      data-testid="data-sub-rail"
    >
      {/* Rail header */}
      <div className="px-4 py-3 border-b border-[var(--color-border)]">
        <h3
          className="text-xs font-semibold uppercase tracking-wider text-[var(--color-text-label)]"
          style={{ fontFamily: "'DM Serif Display', Georgia, serif" }}
        >
          Data Tools
        </h3>
      </div>

      {/* Rail items */}
      <nav className="flex-1 overflow-y-auto py-2" role="tablist" aria-label="Data stage tools">
        {items.map((item) => {
          const isActive = item.id === activeId;
          const Icon = item.icon;

          return (
            <button
              key={item.id}
              role="tab"
              aria-selected={isActive}
              aria-controls={`data-panel-${item.id}`}
              onClick={() => onSelect(item.id)}
              className={`
                w-full flex items-center gap-3 px-4 py-3 text-left
                transition-colors duration-150
                hover:bg-[var(--color-bg-hover)]
                ${isActive
                  ? 'bg-[var(--color-bg-elevated)] border-l-[3px] border-l-[var(--color-brand)]'
                  : 'border-l-[3px] border-l-transparent'
                }
              `}
            >
              <Icon
                className={`w-4 h-4 flex-shrink-0 ${
                  isActive ? 'text-[var(--color-brand)]' : 'text-[var(--color-text-muted)]'
                }`}
              />
              <span
                className={`text-sm font-medium ${
                  isActive
                    ? 'text-[var(--color-text-primary)]'
                    : 'text-[var(--color-text-secondary)]'
                }`}
              >
                {item.label}
              </span>
            </button>
          );
        })}
      </nav>
    </aside>
  );
}

interface ContentPanelProps {
  item: SubRailItem;
  caseId?: string;
}

function ContentPanel({ item, caseId }: ContentPanelProps): React.JSX.Element {
  const Component = item.component;

  // Render the lazy-loaded component with appropriate props
  const renderComponent = () => {
    // For components that require caseId, inject it if available
    if (item.requiresCaseId && caseId) {
      // Different components expect different prop names
      if (item.id === 'reconciliation' || item.id === 'anomalies') {
        return <Component reportId={caseId} />;
      }
      if (item.id === 'data-quality' || item.id === 'cleanup') {
        return <Component claimId={caseId} />;
      }
    }

    // Default: render without props
    return <Component />;
  };

  return (
    <div
      id={`data-panel-${item.id}`}
      role="tabpanel"
      aria-labelledby={`data-tab-${item.id}`}
      className="flex-1 overflow-auto bg-[var(--color-bg-base)]"
      data-testid={`data-panel-${item.id}`}
    >
      <Suspense
        fallback={
          <div className="p-8 text-center text-[var(--color-text-muted)]">
            <div className="animate-pulse">Loading {item.label}...</div>
          </div>
        }
      >
        <ErrorBoundary fallback={<ComponentError name={item.label} />}>
          {renderComponent()}
        </ErrorBoundary>
      </Suspense>
    </div>
  );
}

interface ComponentErrorProps {
  name: string;
}

function ComponentError({ name }: ComponentErrorProps): React.JSX.Element {
  return (
    <div className="p-8 text-center">
      <AlertTriangle className="mx-auto mb-2 text-[var(--color-text-muted)]" size={24} />
      <h3 className="text-lg font-medium text-[var(--color-text-primary)] mb-1">
        Failed to load {name}
      </h3>
      <p className="text-sm text-[var(--color-text-muted)]">
        The component could not be loaded. It may not exist yet or there was an error.
      </p>
    </div>
  );
}

// Simple error boundary for component load failures
class ErrorBoundary extends React.Component<
  { children: React.ReactNode; fallback: React.ReactNode },
  { hasError: boolean }
> {
  constructor(props: { children: React.ReactNode; fallback: React.ReactNode }) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(): { hasError: boolean } {
    return { hasError: true };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo): void {
    // eslint-disable-next-line no-console
    console.error('DataTab component error:', error, errorInfo);
  }

  render(): React.ReactNode {
    if (this.state.hasError) {
      return this.props.fallback;
    }
    return this.props.children;
  }
}

// ============================================================================
// MAIN COMPONENT
// ============================================================================

export function DataTab({ caseId, defaultSubTab, className = '' }: DataTabProps): React.JSX.Element {
  // Filter items based on availability (caseId required for some)
  const availableItems = useMemo(() => {
    return SUB_RAIL_ITEMS.filter((item) => {
      // If item requires caseId but we don't have one, exclude it
      if (item.requiresCaseId && !caseId) {
        return false;
      }
      return true;
    });
  }, [caseId]);

  // Default to first available item
  const [selectedItem, setSelectedItem] = useState<DataSubTab>(() => {
    if (defaultSubTab && availableItems.some((i) => i.id === defaultSubTab)) {
      return defaultSubTab;
    }
    return availableItems[0]?.id || 'reconciliation';
  });

  // Get the currently selected item config
  const activeItem = useMemo(() => {
    return availableItems.find((i) => i.id === selectedItem) || availableItems[0];
  }, [availableItems, selectedItem]);

  // Handle sub-tab selection
  const handleSelect = (id: DataSubTab): void => {
    setSelectedItem(id);
  };

  // If no items available, show empty state
  if (availableItems.length === 0) {
    return (
      <div className={`flex h-full bg-[var(--color-bg-base)] ${className}`}>
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <Database className="w-12 h-12 text-[var(--color-text-muted)] mx-auto mb-4" />
            <h3 className="text-lg font-medium text-[var(--color-text-primary)] mb-1">
              No Data Tools Available
            </h3>
            <p className="text-sm text-[var(--color-text-muted)]">
              Open a case to access data tools.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className={`flex h-full bg-[var(--color-bg-base)] ${className}`}
      data-testid="data-tab"
    >
      <SubRail items={availableItems} activeId={selectedItem} onSelect={handleSelect} />
      {activeItem && <ContentPanel item={activeItem} caseId={caseId} />}
    </div>
  );
}

export default DataTab;
